/**
 * i18n for the `validation` feature — FR default + AR (RTL). Registered once in the module's
 * onRegister hook. Keys are namespaced under `validation.*` / `nav.*` so no central dictionary owns
 * them. Layout mirrors automatically under dir="rtl" (CSS logical properties); mono numerics stay
 * LTR via .nv-mono.nv-ltr-island in the JSX.
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // nav
  "nav.section.production": "Production",
  "nav.validation": "Validation biologique",
  "nav.validation.worklist": "Liste à valider",
  "nav.validation.escalade": "Escalades critiques",
  "nav.validation.recall": "Rappels de lot",
  "nav.validation.bgreview": "Revue break-glass",

  // worklist
  "validation.title": "Validation biologique",
  "validation.subtitle": "Signature biologique des résultats (D-15)",
  "validation.worklist.title": "Résultats à valider",
  "validation.worklist.count": "{n} résultat(s) en attente",
  "validation.worklist.empty.title": "Aucun résultat à valider",
  "validation.worklist.empty.desc": "La paillasse n'a transmis aucun résultat brut ou repris.",
  "validation.worklist.criticalsFirst": "Valeurs critiques en tête",

  // tabs / filters
  "validation.tab.all": "Tous",
  "validation.tab.critical": "Critiques",
  "validation.tab.corrections": "Corrections à re-valider",
  "validation.tab.count": "{n}",

  // columns
  "validation.col.select": "Sélection",
  "validation.col.result": "Résultat",
  "validation.col.value": "Valeur",
  "validation.col.unit": "Unité",
  "validation.col.flag": "Drapeau",
  "validation.col.status": "Statut",
  "validation.col.received": "Reçu le",

  // flags
  "validation.flag.normal": "Dans l'intervalle",
  "validation.flag.bas": "Bas",
  "validation.flag.haut": "Haut",
  "validation.flag.critique": "Valeur panique",

  // result status
  "validation.status.brut": "Brut",
  "validation.status.repris": "Repris",
  "validation.status.correction": "Correction (D-24)",

  // selection bar
  "validation.selection.count": "{n} sélectionné(s)",
  "validation.selection.criticals": "dont {n} critique(s)",
  "validation.action.signSelected": "Signer la sélection",
  "validation.action.signOne": "Signer",
  "validation.action.reprise": "Reprendre",
  "validation.action.reject": "Rejeter",
  "validation.action.selectAll": "Tout sélectionner",
  "validation.action.clear": "Effacer la sélection",
  "validation.shortcut.sign": "Raccourci : F8 pour signer la sélection",

  // sign-off modal
  "validation.sign.title": "Confirmer la signature biologique",
  "validation.sign.decision": "Décision",
  "validation.sign.decision.valide": "Valider",
  "validation.sign.decision.rejete": "Rejeter (retour paillasse)",
  "validation.sign.decision.repris": "Reprendre (retour paillasse)",
  "validation.sign.comment": "Commentaire",
  "validation.sign.comment.hint": "Optionnel — joint à la signature (médico-légal).",
  "validation.sign.criticalGate.title": "Acquittement des valeurs critiques requis",
  "validation.sign.criticalGate.desc":
    "Une valeur critique ne peut être validée sans acquittement explicite (D-15). Cochez chaque valeur panique avant de signer.",
  "validation.sign.ackOne": "J'acquitte cette valeur critique",
  "validation.sign.sansRef.badge": "Sans référence",
  "validation.sign.sansRef.ackOne": "Je signe sans intervalle de référence",
  "validation.sign.sansRefGate.title": "Résultat(s) sans intervalle de référence",
  "validation.sign.sansRefGate.desc":
    "Aucun intervalle de référence ne couvre ce(s) résultat(s) (EG-46). La validation exige un acquittement explicite « signé sans référence » — signalez le trou de couverture au responsable du catalogue.",
  "validation.sign.sansRef.ackRequired": "{n} acquittement(s) sans-référence manquant(s)",
  "validation.sign.confirm": "Signer définitivement",
  "validation.sign.immutableNote": "La signature est immuable : une re-validation est une nouvelle entrée.",
  "validation.sign.ok": "{n} résultat(s) signé(s).",
  "validation.sign.okEscalade": "{n} signé(s) — {e} escalade(s) critique(s) ouverte(s).",
  "validation.sign.ackRequired": "Acquittement requis pour {n} valeur(s) critique(s) avant signature.",
  "validation.sign.error": "Échec de la signature.",
  "validation.sign.conflict": "Résultat déjà validé — la re-validation passe par une correction.",

  // correction re-open
  "validation.correction.title": "Corrections à re-valider (D-24)",
  "validation.correction.desc":
    "Un résultat corrigé par la paillasse remplace un résultat déjà validé et doit être re-signé.",
  "validation.correction.badge": "Correction",
  "validation.correction.version": "Version {n}",
  "validation.correction.supersedes": "Remplace le résultat #{id}",
  "validation.correction.empty.title": "Aucune correction en attente",
  "validation.correction.empty.desc": "Aucun résultat corrigé ne nécessite une nouvelle signature.",

  // escalade view
  "validation.escalade.title": "Escalades de valeurs critiques (D-29)",
  "validation.escalade.subtitle": "Boucle d'escalade au prescripteur avec read-back documenté (ISO 15189).",
  "validation.escalade.lookup": "Identifiant d'escalade",
  "validation.escalade.lookup.hint":
    "Le serveur n'expose pas la liste des escalades. Saisissez l'identifiant d'escalade ouvert à la signature pour la traiter.",
  "validation.escalade.open": "Ouvrir",
  "validation.escalade.recent": "Escalades ouvertes durant cette session",
  "validation.escalade.recent.empty": "Aucune escalade ouverte durant cette session.",
  // NOVA-051 — the durable worklist (GET /validation/escalades?statut=a_acquitter)
  "validation.escalade.worklist": "Escalades à acquitter",
  "validation.escalade.worklist.empty": "Aucune escalade en attente d'acquittement.",
  "validation.escalade.worklist.loading": "Chargement des escalades…",
  "validation.escalade.worklist.error": "Impossible de charger les escalades.",
  "validation.escalade.fromResult": "Résultat #{id}",
  "validation.escalade.statut": "Statut",
  "validation.escalade.statut.a_acquitter": "À acquitter",
  "validation.escalade.statut.acquitte": "Acquittée",
  "validation.escalade.statut.echec": "Échec — repli biologiste",
  "validation.escalade.canal": "Canal",
  "validation.escalade.canal.push_seam2": "Notification sécurisée (Novera MED)",
  "validation.escalade.canal.telephone": "Téléphone",
  "validation.escalade.canal.worklist_biologiste": "Liste biologiste",
  "validation.escalade.tentatives": "Tentatives",
  "validation.escalade.nextSweep": "Prochaine relance",
  "validation.escalade.attempts.title": "Journal des tentatives",
  "validation.escalade.attempts.empty": "Aucune tentative enregistrée.",
  "validation.escalade.attempts.channel": "Canal",
  "validation.escalade.attempts.outcome": "Résultat",
  "validation.escalade.attempts.when": "Le",

  // record a ladder attempt
  "validation.tentative.title": "Enregistrer une tentative",
  "validation.tentative.canal": "Canal",
  "validation.tentative.resultat": "Résultat de la tentative",
  "validation.tentative.resultat.envoye": "Envoyée",
  "validation.tentative.resultat.lu": "Lue (accusé)",
  "validation.tentative.resultat.sans_reponse": "Sans réponse",
  "validation.tentative.resultat.echec_technique": "Échec technique",
  "validation.tentative.ref": "Référence de notification",
  "validation.tentative.ref.hint": "Référence opaque sans donnée de santé (PHI-safe).",
  "validation.tentative.submit": "Enregistrer la tentative",
  "validation.tentative.ok": "Tentative enregistrée.",
  "validation.tentative.luWarn": "Un accusé de lecture ne suffit pas : un read-back actif est requis (VAL-2).",

  // read-back acknowledgment
  "validation.ack.title": "Acquittement par read-back",
  "validation.ack.desc":
    "La boucle se ferme uniquement sur un read-back (restitution verbatim du prescripteur ou transcription de l'appel). Un accusé de réception ne suffit pas.",
  "validation.ack.readBack": "Read-back (verbatim)",
  "validation.ack.readBack.hint": "Restitution exacte de la valeur critique par le prescripteur.",
  "validation.ack.by": "Acquittée par",
  "validation.ack.by.hint": "Référence du prescripteur ou du personnel ayant pris l'appel.",
  "validation.ack.submit": "Clôturer l'escalade",
  "validation.ack.ok": "Escalade acquittée — read-back enregistré.",
  "validation.ack.required": "Un read-back est requis pour clôturer l'escalade.",

  // break-glass
  "validation.breakglass.title": "Break-glass — devoir d'alerte (VAL-3)",
  "validation.breakglass.desc":
    "À utiliser quand aucun consentement de publication n'existe. Portée limitée au fait critique uniquement ; revue obligatoire a posteriori.",
  "validation.breakglass.open": "Déclencher le break-glass",
  "validation.breakglass.justification": "Justification",
  "validation.breakglass.justification.hint": "Obligatoire — un break-glass sans justification est impossible.",
  "validation.breakglass.medecin": "Référence du prescripteur",
  "validation.breakglass.medecin.hint": "Optionnel.",
  "validation.breakglass.scope": "Portée : fait critique uniquement (jamais le dossier complet).",
  "validation.breakglass.submit": "Confirmer le break-glass",
  "validation.breakglass.ok": "Break-glass enregistré — en attente de revue.",
  "validation.breakglass.required": "Une justification est obligatoire.",

  // NOVA-028 — lot-recall worklist (INV-4 → D-24)
  "validation.recall.title": "Rappels de lot — résultats à re-décider (D-24)",
  "validation.recall.subtitle":
    "Résultats déjà diffusés produits sur un lot de réactif rappelé (quarantaine) ou périmé. Re-décidez chacun via le flux de correction.",
  "validation.recall.count": "{n} résultat(s) diffusé(s) concerné(s)",
  "validation.recall.lot": "Lot {lot} — {reactif}",
  "validation.recall.lotStatut.quarantaine": "Lot en quarantaine (rappel)",
  "validation.recall.lotStatut.perime": "Lot périmé",
  "validation.recall.affected": "{n} résultat(s) concerné(s)",
  "validation.recall.col.result": "Résultat",
  "validation.recall.col.value": "Valeur",
  "validation.recall.col.unit": "Unité",
  "validation.recall.col.flag": "Drapeau",
  "validation.recall.col.status": "Statut",
  "validation.recall.correct": "Corriger (D-24)",
  "validation.recall.empty.title": "Aucun rappel de lot en attente",
  "validation.recall.empty.desc": "Aucun résultat diffusé n'est lié à un lot rappelé ou périmé.",

  // NOVA-023 — break-glass post-hoc review (VAL-3)
  "validation.bgreview.title": "Revue a posteriori des break-glass (VAL-3)",
  "validation.bgreview.subtitle": "Revue obligatoire de chaque dérogation au devoir d'alerte par le responsable.",
  "validation.bgreview.queue": "Break-glass en attente de revue",
  "validation.bgreview.pending": "À revoir",
  "validation.bgreview.fromEscalade": "Escalade #{id}",
  "validation.bgreview.justification": "Justification",
  "validation.bgreview.col.id": "Référence",
  "validation.bgreview.col.escalade": "Escalade",
  "validation.bgreview.col.justification": "Justification",
  "validation.bgreview.col.status": "Statut",
  "validation.bgreview.review": "Revoir",
  "validation.bgreview.outcome": "Conclusion de la revue",
  "validation.bgreview.outcome.hint": "Obligatoire — le jugement enregistré (scellé dans la chaîne d'audit).",
  "validation.bgreview.outcome.justifie": "Justifié",
  "validation.bgreview.outcome.non_justifie": "Non justifié",
  "validation.bgreview.outcome.a_surveiller": "À surveiller",
  "validation.bgreview.notes": "Notes",
  "validation.bgreview.notes.hint": "Optionnel — précisions sur la décision.",
  "validation.bgreview.submit": "Clôturer la revue",
  "validation.bgreview.done": "Revue clôturée.",
  "validation.bgreview.error": "Échec de la revue.",
  "validation.bgreview.empty.title": "Aucun break-glass en attente de revue",
  "validation.bgreview.empty.desc": "Toutes les dérogations au devoir d'alerte ont été revues.",

  // generic
  "validation.cancel": "Annuler",
  "validation.close": "Fermer",
  "validation.required": "Champ requis.",
  "validation.id.placeholder": "ex. 1024",
};

const AR: Record<string, string> = {
  "nav.section.production": "الإنتاج",
  "nav.validation": "المصادقة البيولوجية",
  "nav.validation.worklist": "قائمة المصادقة",
  "nav.validation.escalade": "التصعيدات الحرجة",
  "nav.validation.recall": "سحب الدفعات",
  "nav.validation.bgreview": "مراجعة break-glass",

  "validation.title": "المصادقة البيولوجية",
  "validation.subtitle": "التوقيع البيولوجي على النتائج (D-15)",
  "validation.worklist.title": "نتائج بانتظار المصادقة",
  "validation.worklist.count": "{n} نتيجة قيد الانتظار",
  "validation.worklist.empty.title": "لا توجد نتائج للمصادقة",
  "validation.worklist.empty.desc": "لم ترسل المخبرية أي نتيجة خام أو معادة.",
  "validation.worklist.criticalsFirst": "القيم الحرجة في المقدمة",

  "validation.tab.all": "الكل",
  "validation.tab.critical": "حرجة",
  "validation.tab.corrections": "تصحيحات لإعادة المصادقة",
  "validation.tab.count": "{n}",

  "validation.col.select": "تحديد",
  "validation.col.result": "النتيجة",
  "validation.col.value": "القيمة",
  "validation.col.unit": "الوحدة",
  "validation.col.flag": "العلامة",
  "validation.col.status": "الحالة",
  "validation.col.received": "تاريخ الاستلام",

  "validation.flag.normal": "ضمن المجال",
  "validation.flag.bas": "منخفض",
  "validation.flag.haut": "مرتفع",
  "validation.flag.critique": "قيمة حرجة",

  "validation.status.brut": "خام",
  "validation.status.repris": "معاد",
  "validation.status.correction": "تصحيح (D-24)",

  "validation.selection.count": "{n} محدد",
  "validation.selection.criticals": "منها {n} حرجة",
  "validation.action.signSelected": "توقيع المحدد",
  "validation.action.signOne": "توقيع",
  "validation.action.reprise": "إعادة",
  "validation.action.reject": "رفض",
  "validation.action.selectAll": "تحديد الكل",
  "validation.action.clear": "إلغاء التحديد",
  "validation.shortcut.sign": "اختصار: F8 لتوقيع المحدد",

  "validation.sign.title": "تأكيد التوقيع البيولوجي",
  "validation.sign.decision": "القرار",
  "validation.sign.decision.valide": "مصادقة",
  "validation.sign.decision.rejete": "رفض (إعادة إلى المخبرية)",
  "validation.sign.decision.repris": "إعادة (إلى المخبرية)",
  "validation.sign.comment": "تعليق",
  "validation.sign.comment.hint": "اختياري — يُرفق بالتوقيع (طبي-قانوني).",
  "validation.sign.criticalGate.title": "الإقرار بالقيم الحرجة مطلوب",
  "validation.sign.criticalGate.desc":
    "لا يمكن المصادقة على قيمة حرجة دون إقرار صريح (D-15). أقرّ بكل قيمة حرجة قبل التوقيع.",
  "validation.sign.ackOne": "أقرّ بهذه القيمة الحرجة",
  "validation.sign.confirm": "توقيع نهائي",
  "validation.sign.immutableNote": "التوقيع غير قابل للتعديل: إعادة المصادقة إدخال جديد.",
  "validation.sign.ok": "تم توقيع {n} نتيجة.",
  "validation.sign.okEscalade": "تم توقيع {n} — فُتحت {e} تصعيدات حرجة.",
  "validation.sign.ackRequired": "الإقرار مطلوب لـ {n} قيمة حرجة قبل التوقيع.",
  "validation.sign.error": "فشل التوقيع.",
  "validation.sign.conflict": "النتيجة مصادق عليها مسبقًا — إعادة المصادقة تتم عبر تصحيح.",

  "validation.correction.title": "تصحيحات لإعادة المصادقة (D-24)",
  "validation.correction.desc": "نتيجة صححتها المخبرية تحل محل نتيجة مصادق عليها ويجب إعادة توقيعها.",
  "validation.correction.badge": "تصحيح",
  "validation.correction.version": "الإصدار {n}",
  "validation.correction.supersedes": "يحل محل النتيجة رقم {id}",
  "validation.correction.empty.title": "لا توجد تصحيحات قيد الانتظار",
  "validation.correction.empty.desc": "لا توجد نتيجة مصححة تتطلب توقيعًا جديدًا.",

  "validation.escalade.title": "تصعيدات القيم الحرجة (D-29)",
  "validation.escalade.subtitle": "حلقة تصعيد إلى الطبيب الواصف مع read-back موثّق (ISO 15189).",
  "validation.escalade.lookup": "معرّف التصعيد",
  "validation.escalade.lookup.hint":
    "لا يوفّر الخادم قائمة التصعيدات. أدخل معرّف التصعيد المفتوح عند التوقيع لمعالجته.",
  "validation.escalade.open": "فتح",
  "validation.escalade.recent": "تصعيدات فُتحت خلال هذه الجلسة",
  "validation.escalade.recent.empty": "لا توجد تصعيدات فُتحت خلال هذه الجلسة.",
  // NOVA-051
  "validation.escalade.worklist": "تصعيدات بانتظار الإقرار",
  "validation.escalade.worklist.empty": "لا توجد تصعيدات بانتظار الإقرار.",
  "validation.escalade.worklist.loading": "جارٍ تحميل التصعيدات…",
  "validation.escalade.worklist.error": "تعذّر تحميل التصعيدات.",
  "validation.escalade.fromResult": "النتيجة رقم {id}",
  "validation.escalade.statut": "الحالة",
  "validation.escalade.statut.a_acquitter": "بانتظار الإقرار",
  "validation.escalade.statut.acquitte": "تم الإقرار",
  "validation.escalade.statut.echec": "فشل — رجوع إلى البيولوجي",
  "validation.escalade.canal": "القناة",
  "validation.escalade.canal.push_seam2": "إشعار آمن (Novera MED)",
  "validation.escalade.canal.telephone": "هاتف",
  "validation.escalade.canal.worklist_biologiste": "قائمة البيولوجي",
  "validation.escalade.tentatives": "المحاولات",
  "validation.escalade.nextSweep": "المحاولة التالية",
  "validation.escalade.attempts.title": "سجل المحاولات",
  "validation.escalade.attempts.empty": "لا توجد محاولات مسجلة.",
  "validation.escalade.attempts.channel": "القناة",
  "validation.escalade.attempts.outcome": "النتيجة",
  "validation.escalade.attempts.when": "في",

  "validation.tentative.title": "تسجيل محاولة",
  "validation.tentative.canal": "القناة",
  "validation.tentative.resultat": "نتيجة المحاولة",
  "validation.tentative.resultat.envoye": "أُرسلت",
  "validation.tentative.resultat.lu": "قُرئت (إشعار)",
  "validation.tentative.resultat.sans_reponse": "دون رد",
  "validation.tentative.resultat.echec_technique": "فشل تقني",
  "validation.tentative.ref": "مرجع الإشعار",
  "validation.tentative.ref.hint": "مرجع مبهم دون بيانات صحية (PHI-safe).",
  "validation.tentative.submit": "تسجيل المحاولة",
  "validation.tentative.ok": "تم تسجيل المحاولة.",
  "validation.tentative.luWarn": "إشعار القراءة لا يكفي: يلزم read-back فعلي (VAL-2).",

  "validation.ack.title": "الإقرار عبر read-back",
  "validation.ack.desc":
    "تُغلق الحلقة فقط عبر read-back (إعادة حرفية من الطبيب الواصف أو تدوين المكالمة). إشعار الاستلام لا يكفي.",
  "validation.ack.readBack": "read-back (حرفي)",
  "validation.ack.readBack.hint": "إعادة دقيقة للقيمة الحرجة من طرف الطبيب الواصف.",
  "validation.ack.by": "أقرّ من طرف",
  "validation.ack.by.hint": "مرجع الطبيب الواصف أو الموظف الذي تلقى المكالمة.",
  "validation.ack.submit": "إغلاق التصعيد",
  "validation.ack.ok": "تم الإقرار بالتصعيد — سُجّل read-back.",
  "validation.ack.required": "يلزم read-back لإغلاق التصعيد.",

  "validation.breakglass.title": "Break-glass — واجب التنبيه (VAL-3)",
  "validation.breakglass.desc":
    "يُستعمل عند غياب موافقة النشر. النطاق محصور في الواقعة الحرجة فقط؛ مراجعة لاحقة إلزامية.",
  "validation.breakglass.open": "تشغيل break-glass",
  "validation.breakglass.justification": "التبرير",
  "validation.breakglass.justification.hint": "إلزامي — break-glass دون تبرير مستحيل.",
  "validation.breakglass.medecin": "مرجع الطبيب الواصف",
  "validation.breakglass.medecin.hint": "اختياري.",
  "validation.breakglass.scope": "النطاق: الواقعة الحرجة فقط (لا الملف الكامل أبدًا).",
  "validation.breakglass.submit": "تأكيد break-glass",
  "validation.breakglass.ok": "تم تسجيل break-glass — بانتظار المراجعة.",
  "validation.breakglass.required": "التبرير إلزامي.",

  // NOVA-028 — سحب الدفعات
  "validation.recall.title": "سحب الدفعات — نتائج لإعادة القرار (D-24)",
  "validation.recall.subtitle":
    "نتائج منشورة أُنتجت على دفعة كاشف مسحوبة (حجر) أو منتهية الصلاحية. أعد القرار في كل منها عبر مسار التصحيح.",
  "validation.recall.count": "{n} نتيجة منشورة معنية",
  "validation.recall.lot": "الدفعة {lot} — {reactif}",
  "validation.recall.lotStatut.quarantaine": "دفعة في الحجر (سحب)",
  "validation.recall.lotStatut.perime": "دفعة منتهية الصلاحية",
  "validation.recall.affected": "{n} نتيجة معنية",
  "validation.recall.col.result": "النتيجة",
  "validation.recall.col.value": "القيمة",
  "validation.recall.col.unit": "الوحدة",
  "validation.recall.col.flag": "العلامة",
  "validation.recall.col.status": "الحالة",
  "validation.recall.correct": "تصحيح (D-24)",
  "validation.recall.empty.title": "لا توجد عمليات سحب دفعات قيد الانتظار",
  "validation.recall.empty.desc": "لا توجد نتيجة منشورة مرتبطة بدفعة مسحوبة أو منتهية.",

  // NOVA-023 — مراجعة break-glass اللاحقة
  "validation.bgreview.title": "المراجعة اللاحقة لعمليات break-glass (VAL-3)",
  "validation.bgreview.subtitle": "مراجعة إلزامية لكل استثناء من واجب التنبيه من طرف المسؤول.",
  "validation.bgreview.queue": "break-glass بانتظار المراجعة",
  "validation.bgreview.pending": "للمراجعة",
  "validation.bgreview.fromEscalade": "تصعيد رقم {id}",
  "validation.bgreview.justification": "التبرير",
  "validation.bgreview.col.id": "المرجع",
  "validation.bgreview.col.escalade": "التصعيد",
  "validation.bgreview.col.justification": "التبرير",
  "validation.bgreview.col.status": "الحالة",
  "validation.bgreview.review": "مراجعة",
  "validation.bgreview.outcome": "خلاصة المراجعة",
  "validation.bgreview.outcome.hint": "إلزامي — الحكم المسجّل (مختوم في سلسلة التدقيق).",
  "validation.bgreview.outcome.justifie": "مبرَّر",
  "validation.bgreview.outcome.non_justifie": "غير مبرَّر",
  "validation.bgreview.outcome.a_surveiller": "للمتابعة",
  "validation.bgreview.notes": "ملاحظات",
  "validation.bgreview.notes.hint": "اختياري — تفاصيل القرار.",
  "validation.bgreview.submit": "إغلاق المراجعة",
  "validation.bgreview.done": "تم إغلاق المراجعة.",
  "validation.bgreview.error": "فشل المراجعة.",
  "validation.bgreview.empty.title": "لا توجد break-glass بانتظار المراجعة",
  "validation.bgreview.empty.desc": "تمت مراجعة جميع استثناءات واجب التنبيه.",

  "validation.cancel": "إلغاء",
  "validation.close": "إغلاق",
  "validation.required": "حقل مطلوب.",
  "validation.id.placeholder": "مثال: 1024",
};

/** Called once from the FeatureModule.onRegister hook. */
export function registerValidationMessages(): void {
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
